import { useState, useEffect, useRef } from "react";
import { FileTree, AutoPlayControl, UploadButton } from "../components";
import { filesService } from "../services";
import { FileIndex, UserSession } from "../types";
import { Container, Row, Col, Offcanvas, Stack, Button } from "react-bootstrap";
import { Replay } from "vimond-replay";
import "vimond-replay/index.css";
import { VideoStreamState } from "vimond-replay/default-player/Replay";
import EditButton from "../components/editControls";

function FilesPage() {
  const [sessionLoaded, setSessionLoaded] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<FileIndex>();
  const [fileList, setFileList] = useState<FileIndex[]>([]);
  const [fileUrl, setFileUrl] = useState<string>();
  const [fileContent, setFileContent] = useState<string>("");
  // const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false);
  const [videoFinished, setVideoFinished] = useState<boolean>(false);
  const [userSession, setUserSession] = useState<UserSession>({
    autoplay: false,
    playedIndices: [],
    searchText: "",
    selectedFileNames: [],
    shuffle: false,
    typeFilter: "all",
    playedSeconds: 0,
  });

  const playbackState = useRef<VideoStreamState>({});

  const refreshFileList = async () => {
    const files = await filesService.listFiles();
    setFileList(files);
    if (selectedItem) {
      files.forEach((f) => {
        if (f.fileName == selectedItem.fileName) {
          setSelectedItem(f);
        }
      });
    }
  };
  const fetchUserSession = async () => {
    const session = await filesService.getUserSession();
    setUserSession(session);
    setSessionLoaded(true);
  };
  const selectItem = (item: FileIndex | undefined) => {
    setSelectedItem(item);
  };

  const isUrlExpiringSoon = (url: string): boolean => {
    // CloudFront signed URLs encode expiry as ?Expires=<unix_seconds>
    const match = url.match(/[?&]Expires=(\d+)/);
    if (!match) return true; // no expiry param — treat as stale
    const expiresAt = parseInt(match[1], 10) * 1000; // convert to ms
    const fifteenMins = 15 * 60 * 1000;
    return Date.now() + fifteenMins >= expiresAt;
  };

  useEffect(() => {
    async function getFile() {
      setVideoFinished(false);
      if (selectedItem === undefined) {
        setFileUrl("");
        setFileContent("");
      } else if (["video", "image"].indexOf(selectedItem.fileType) >= 0) {
        const needsFetch =
          !fileUrl ||
          fileUrl.indexOf(selectedItem.fileName) < 0 ||
          isUrlExpiringSoon(fileUrl);
        if (needsFetch) {
          setFileUrl(await filesService.getFileUrl(selectedItem.fileName));
          setFileContent("");
        }
      } else if (selectedItem.fileType === "text") {
        setFileUrl("");
        setFileContent(await filesService.getFile(selectedItem.fileName));
      } else if (selectedItem.fileType === "photoAlbum") {
        setFileUrl("");
        setFileContent("");
        // setPhotoUrls(
        //   await filesService.getPhotoAlbumUrls(selectedItem.fileName),
        // );
      } else {
        console.log("unhandled", selectedItem);
      }
    }
    getFile();
  }, [selectedItem]);

  const updateUserSession = async (session: UserSession) => {
    if (sessionLoaded) {
      await filesService.saveUserSession(session);
      setUserSession(session);
    }
  };
  const saveUserSession = (session: UserSession) => {
    if (sessionLoaded) {
      const timeoutId = setTimeout(async () => {
        await filesService.saveUserSession(session);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    refreshFileList();
    fetchUserSession();
  }, []);

  return sessionLoaded ? (
    <>
      <Offcanvas show={showMobileNav} onHide={() => setShowMobileNav(false)}>
        <Offcanvas.Header closeButton>Library</Offcanvas.Header>
        <Offcanvas.Body>
          <FileTree
            fileList={fileList}
            refreshFileList={refreshFileList}
            selectedItem={selectedItem}
            setSelectedItem={selectItem}
            videoFinished={videoFinished}
            userSession={userSession}
            updateUserSession={updateUserSession}
          />
        </Offcanvas.Body>
      </Offcanvas>
      <Container fluid className="h-100">
        <Row className="flex-fill h-100" style={{ overflowY: "scroll" }}>
          <Col
            lg="3"
            className="h-100 d-none d-md-inline"
            // style={{ overflowY: "scroll" }}
          >
            <FileTree
              fileList={fileList}
              refreshFileList={refreshFileList}
              selectedItem={selectedItem}
              setSelectedItem={selectItem}
              videoFinished={videoFinished}
              userSession={userSession}
              updateUserSession={updateUserSession}
            />
          </Col>
          <Col className="flex-shrink h-100 mh-100">
            <Row className="py-2">
              <Stack
                direction="horizontal"
                gap={1}
                className="py-1 pe-4 d-none d-lg-flex"
              >
                <h2
                  className="me-auto text-wrap"
                  style={{ overflowWrap: "break-word" }}
                >
                  {selectedItem?.displayName}
                </h2>
                <EditButton
                  selectedItem={selectedItem}
                  fileList={fileList}
                  refreshFileList={refreshFileList}
                />
                {selectedItem && selectedItem.fileType === "video" && (
                  <AutoPlayControl
                    userSession={userSession}
                    updateUserSession={updateUserSession}
                  />
                )}
              </Stack>
              {/* Mobile stack*/}
              <div className="d-xs-inline d-lg-none">
                <Stack direction="horizontal" gap={1} className="py-1 pe-4">
                  <h2
                    className="me-auto text-wrap"
                    style={{ overflowWrap: "break-word" }}
                  >
                    {selectedItem?.displayName}
                  </h2>
                  <Button onClick={() => setShowMobileNav(true)}>
                    Library
                  </Button>
                </Stack>
                <Stack direction="horizontal" gap={1} className="py-1 pe-4">
                  {selectedItem && selectedItem.fileType === "video" && (
                    <>
                      <AutoPlayControl
                        userSession={userSession}
                        updateUserSession={updateUserSession}
                      />
                      <div className="ms-auto" />
                    </>
                  )}
                  <EditButton
                    selectedItem={selectedItem}
                    fileList={fileList}
                    refreshFileList={refreshFileList}
                  />
                </Stack>
              </div>
            </Row>
            <Row className="mr-1 flex-grow-1">
              {/* nothing selected */}
              <div
                className={selectedItem === undefined ? "d-inline" : "d-none"}
              >
                Nothing selected
              </div>
              {/* image selected */}
              <img
                className={
                  selectedItem?.fileType === "image" ? "d-inline" : "d-none"
                }
                alt="Fetching..."
                src={fileUrl}
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "scale-down",
                }}
              />
              {/* video selected */}
              <div
                className={
                  selectedItem?.fileType === "video" ? "d-inline" : "d-none"
                }
              >
                <Replay
                  source={{
                    streamUrl: fileUrl || "",
                    startPosition: userSession.playedSeconds,
                  }}
                  initialPlaybackProps={{ isPaused: !userSession.autoplay }}
                  options={{
                    controls: {
                      includeControls: [
                        "playPauseButton",
                        "timeline",
                        "timeDisplay",
                        "volume",
                        "fullscreenButton",
                        "airPlayButton",
                      ],
                    },
                  }}
                  onStreamStateChange={(changedState: VideoStreamState) => {
                    playbackState.current = {
                      ...playbackState.current,
                      ...changedState,
                    };
                    if (
                      changedState.position !== 0 &&
                      playbackState.current.duration &&
                      playbackState.current.duration > 0 &&
                      playbackState.current.playState !== "starting" &&
                      playbackState.current.playState !== "inactive" &&
                      playbackState.current.isPaused === true
                    ) {
                      if (
                        playbackState.current.position ===
                        playbackState.current.duration
                      ) {
                        playbackState.current = {
                          ...playbackState.current,
                          position: -1,
                        };
                        setVideoFinished(true);
                      } else if (playbackState.current.position) {
                        saveUserSession({
                          ...userSession,
                          playedSeconds: playbackState.current.position,
                        });
                      }
                    }
                  }}
                />
              </div>
              {/* text file selected */}
              <div
                className={`h-100 ${
                  selectedItem?.fileType === "text" ? "d-inline" : "d-none"
                }`}
                style={{ overflowY: "scroll" }}
              >
                <div
                  style={{
                    whiteSpace: "pre-line",
                    paddingBottom: "50px",
                  }}
                >
                  {fileContent}
                </div>
              </div>
              {/* photo album selected */}
              <div
                className={`${
                  selectedItem?.fileType === "photoAlbum"
                    ? "d-flex flex-column"
                    : "d-none"
                }`}
              >
                <div
                  style={{
                    whiteSpace: "pre-line",
                    paddingBottom: "50px",
                  }}
                >
                  <UploadButton
                    refreshFileList={refreshFileList}
                    treePath={selectedItem ? [selectedItem] : []}
                    canDeleteFolder={false}
                    isAddPhotos={true}
                  />
                </div>
                <div
                  className="flex-fill h-100 pb-5"
                  style={{ overflowY: "scroll" }}
                >
                  <div
                    className=" d-flex flex-wrap"
                    style={{ gap: "10px", paddingBottom: "50px" }}
                  >
                    {selectedItem?.children?.map((child, idx) => (
                      <img
                        key={idx}
                        alt={`Photo ${idx + 1}`}
                        src={`data:image/jpeg;base64,${child.thumbnail}`}
                        style={{
                          height: "200px",
                          width: "200px",
                          objectFit: "cover",
                        }}
                      />
                    ))}
                    {/* {photoUrls &&
                      photoUrls.map((url, idx) => (
                        <img
                          key={idx}
                          alt={`Photo ${idx + 1}`}
                          src={url}
                          style={{
                            height: "200px",
                            width: "200px",
                            objectFit: "cover",
                          }}
                        />
                      ))} */}
                  </div>
                </div>
              </div>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  ) : (
    <div>Loading data...</div>
  );
}

export default FilesPage;
